import {
  fetchResourcesJson,
  propertyUrlApi,
  flyersUrlApi,
  flyerUrlApi,
  flyerStoresUrlApi,
  flyerStoreOpeningHoursUrlApi,
  flyerFlyerGibsUrlApi,
  flyerFlyerGibsProductUrlApi,
  flyerWithCustomLabelUrlApi,
  nextFlyerAvailabilityUrlApi,
  //--- custom ---
  flyersCustomUrlApi,
} from 'utils/apiUrlUtils';

const RESPONSE_STATES = {
  OUT_OF_COORDS: 'OUT_OF_COORDS',
};

export const fetchPropertyResource = async () =>
  new Promise((resolve, reject) => {
    const propertyUrl = propertyUrlApi();
    fetchResourcesJson(propertyUrl)
      .then((response) => {
        if (response.data) {
          let resources = {
            property: { ...response.data, fetched: true },
            flyers: { list: {}, fetchedLocation: undefined },
          };
          resolve(resources);
        } else {
          reject(new Error('non-existent property'));
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

export const fetchFlyersResource = async ({ location, storeCustomLabel, flyerCustomLabel }) =>
  new Promise((resolve, reject) => {
    let flyersUrl;
    if (storeCustomLabel || flyerCustomLabel) {
      flyersUrl = flyersCustomUrlApi(location, storeCustomLabel, flyerCustomLabel);
    } else {
      if (location.lat && location.lng) {
        flyersUrl = flyersUrlApi(location);
      } else {
        reject(new Error('position not found'));
      }
    }

    if (flyersUrl) {
      fetchResourcesJson(flyersUrl)
        .then((response) => {
          if (response.data) {
            resolve(response.data);
          } else {
            resolve({ list: {} });
          }
        })
        .catch((error) => {
          console.error(error);
          resolve({ list: {} });
        });
    }
  });

export const fetchFlyerResource = async ({
  flyerId,
  location,
  storeCustomLabel,
  skipRetryCallWithoutPosition = false,
}) =>
  new Promise((resolve, reject) => {
    let flyerUrl = flyerUrlApi(flyerId, location, storeCustomLabel);

    fetchResourcesJson(flyerUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          if (!skipRetryCallWithoutPosition && response.status === RESPONSE_STATES.OUT_OF_COORDS) {
            resolve(
              fetchFlyerResource({ flyerId, storeCustomLabel, skipRetryCallWithoutPosition: true }),
            );
          } else {
            reject(new Error('flyer not found'));
          }
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

export const fetchStoresResource = ({
  flyerId,
  location,
  limit,
  epochTime,
  skipRetryCallWithoutPosition = false,
  storeCustomLabel,
  parentFlyerId,
}) =>
  new Promise((resolve, reject) => {
    const resourceUrl = flyerStoresUrlApi(
      flyerId,
      location,
      limit,
      epochTime,
      storeCustomLabel,
      parentFlyerId,
    );
    fetchResourcesJson(resourceUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          if (!skipRetryCallWithoutPosition && response.status === RESPONSE_STATES.OUT_OF_COORDS) {
            resolve(
              fetchStoresResource({
                flyerId,
                limit,
                epochTime,
                storeCustomLabel,
                skipRetryCallWithoutPosition: true,
                parentFlyerId,
              }),
            );
          } else {
            resolve([]);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        resolve([]);
      });
  });

export const fetchStoreOpeningHoursResource = (flyerId, storeId) =>
  new Promise((resolve, reject) => {
    const resourceUrl = flyerStoreOpeningHoursUrlApi(flyerId, storeId);
    fetchResourcesJson(resourceUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          resolve({});
        }
      })
      .catch((error) => {
        console.error(error);
        resolve({});
      });
  });

export const fetchFlyerGibsResource = (flyerId) => {
  return new Promise((resolve, reject) => {
    const resourceUrl = flyerFlyerGibsUrlApi(flyerId);
    fetchResourcesJson(resourceUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          resolve([]);
        }
      })
      .catch((error) => {
        console.error(error);
        resolve([]);
      });
  });
};

export const fetchFlyerGibsProductResource = (flyerId, tag) => {
  return new Promise((resolve, reject) => {
    const resourceUrl = flyerFlyerGibsProductUrlApi(flyerId, tag);
    fetchResourcesJson(resourceUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          resolve({});
        }
      })
      .catch((error) => {
        console.error(error);
        resolve({});
      });
  });
};

export const fetchFlyerWithCustomLabelResource = (
  flyerCustomLabel,
  customerStoreCode,
  retailerId,
) => {
  return new Promise((resolve, reject) => {
    const resourceUrl = flyerWithCustomLabelUrlApi(flyerCustomLabel, customerStoreCode, retailerId);
    fetchResourcesJson(resourceUrl)
      .then((response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          reject(new Error('flyer with customer label  not found'));
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const NEXT_AVAILABILITY_TIMEOUT_MS = 3000;

/**
 * Asks store-announcement-format whether the NEXT flyer behind `landingUrl` is published.
 * Resolves `true` only when the API explicitly answers `200` with `available: true`; on any other
 * outcome (no URL, 404, network/CORS error, timeout, unexpected answer) it resolves `false`, so the
 * PDF/NEXT switch is shown only for a NEXT flyer confirmed to be published.
 * @returns {Promise<boolean>}
 */
export const fetchNextFlyerAvailabilityResource = async (landingUrl) => {
  const resourceUrl = nextFlyerAvailabilityUrlApi(landingUrl);
  if (!resourceUrl) return false;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), NEXT_AVAILABILITY_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(resourceUrl, { signal: controller?.signal });
    if (response.status !== 200) return false;
    const body = await response.json();
    return body?.available === true;
  } catch (error) {
    console.warn('[sfViewer:flyerVersion] NEXT availability check failed', error);
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};
